import { Card, CardContent, Box, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import React, { useState } from "react";
import styles from "./ProductCard.module.scss";
import { Link } from "react-router-dom";
import Quantity from "../Quantity";
import { useTranslation } from "react-i18next";
import { numberFormatter } from "../../formatter";

const formatUnit = (u) => {
  if (!u) return "";
  const desc = u.unit_description || u.unit_code || "";
  const factor = u.number_of_items_per_unit;
  return factor ? `${desc} — ${factor} unit${factor !== 1 ? "s" : ""} per ${desc}` : desc;
};

const ProductCard = (props) => {
  const { t } = useTranslation();
  const imageUrl = props.data.OverridenPictureUrl != null
    ? props.data.OverridenPictureUrl
    : props.data.PictureUrl;

  const availableUnits = (props.data.available_units ?? []).map((u) => ({
    ...u,
    unit_code: u.unit_code?.trim(),
  }));
  const hasMultipleUnits = availableUnits.length > 1;
  const hasSingleUnit = availableUnits.length === 1;

  // Pre-select the item's default unit (matches product.Unit), fall back to first
  const defaultUnit =
    availableUnits.find((u) => u.unit_code === props.data.Unit?.trim()) ??
    availableUnits[0] ??
    null;

  const [selectedUnit, setSelectedUnit] = useState(defaultUnit);

  return (
    <Card
      style={{ boxShadow: "none", width: "100%", ...props.sty }}
      sx={{ height: "100%" }}
      className={`${styles.card} card`}
    >
      <CardContent className={styles.cardContent}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <p className={styles.category}>{props.data.Code}</p>
          <Link
            to={`/products/${props.data.ID}`}
            className={styles.title}
            style={{ textAlign: "center", marginTop: "4px", marginBottom: "4px" }}
          >
            {props.data.Description}
          </Link>

          {props.data.ExtraDescription && (
            <p className={styles.extra_description} style={{ textAlign: "center", marginTop: "4px", marginBottom: "4px" }}>
              {props.data.ExtraDescription}
            </p>
          )}

          <div>
            <img src={imageUrl} alt="mainImage" />
          </div>

          <p className={styles.price}>
            {selectedUnit
              ? numberFormatter.format(selectedUnit.price)
              : props.data.price != null
                ? numberFormatter.format(props.data.price)
                : null}
          </p>
          {window.storeSettings?.show_stock_numbers && (
            <p className={styles.stockText}>
              {window.innerWidth < 500
                ? `${t("Stock")}: ${props.totalFreeStock ?? 0}`
                : `${t("Available stock")}: ${props.totalFreeStock ?? 0}`}
            </p>
          )}

          {/* Extra fields the shop owner marked "Show in grid" (highlighted ones stand out) */}
          {Array.isArray(props.data.grid_extra_fields) &&
            props.data.grid_extra_fields.filter((f) => f.value !== null && f.value !== undefined && f.value !== "").length > 0 && (
              <Box sx={{ width: "100%", mt: 0.5, mb: 0.5, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.4 }}>
                {props.data.grid_extra_fields
                  .filter((f) => f.value !== null && f.value !== undefined && f.value !== "")
                  .map((f) =>
                    f.highlight ? (
                      <span
                        key={f.number}
                        style={{
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          color: "#8a6d00",
                          background: "#fff8e1",
                          border: "1px solid #ffca28",
                          borderRadius: 10,
                          padding: "1px 8px",
                        }}
                      >
                        {f.label}: {f.value}
                      </span>
                    ) : (
                      <p
                        key={f.number}
                        style={{ margin: "1px 0", fontSize: "0.75rem", color: "#666", textAlign: "center" }}
                      >
                        {f.label}: <strong>{f.value}</strong>
                      </p>
                    )
                  )}
              </Box>
            )}

          {/* Unit selector — dropdown for multiple units, static label for single unit */}
          {hasMultipleUnits && (
            <Box sx={{ width: "100%", mb: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("Unit")}</InputLabel>
                <Select
                  value={selectedUnit?.unit_code ?? ""}
                  label={t("Unit")}
                  onChange={(e) => {
                    const unit = availableUnits.find(
                      (u) => u.unit_code === e.target.value
                    );
                    setSelectedUnit(unit ?? null);
                  }}
                >
                  {availableUnits.map((u) => (
                    <MenuItem key={u.unit_code} value={u.unit_code}>
                      {formatUnit(u)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          {hasSingleUnit && (
            <Box sx={{ width: "100%", mb: 1, fontSize: "0.85rem", color: "#555", textAlign: "center" }}>
              {formatUnit(selectedUnit)}
            </Box>
          )}
        </Box>

        <Box
          component="div"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: "60%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {hasMultipleUnits && !selectedUnit ? (
              <Box sx={{ color: "text.secondary", fontSize: "0.75rem", textAlign: "center" }}>
                {t("Select a unit")}
              </Box>
            ) : (
              <Quantity
                id={props.data.ID}
                sku={props.data.Code}
                unitCode={selectedUnit?.unit_code ?? null}
              />
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
